import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import { AnalyticsFacade, AutomaticPaymentsAnalytics } from '@amfam/shared/analytics';
import { BillingPaymentPaths } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService
} from '@amfam/shared/utility/shared-services';
import { ConfirmationConfig, ConfirmationStatus, DockingBarService } from '@amfam/ui-kit';

import { AutoPayDeleteUtilService } from '../../util/auto-pay-delete-util.service';

@Component({
  selector: 'ds-auto-pay-delete-confirmation-wrapper',
  templateUrl: './auto-pay-delete-confirmation-wrapper.component.html',
  styleUrls: ['./auto-pay-delete-confirmation-wrapper.component.scss'],
  standalone: false
})
export class AutoPayDeleteConfirmationWrapperComponent implements OnInit {
  hasError$: Observable<boolean>;
  confirmationConfig$: Observable<ConfirmationConfig>;
  billAccountNumber: string;

  deleteSuccessText: Observable<string>;
  deleteFailureText: string;

  constructor(
    private dockingBar: DockingBarService,
    private store: Store,
    private copyService: CopyService,
    private autoPayDeleteUtil: AutoPayDeleteUtilService,
    private route: ActivatedRoute,
    private analyticsFacade: AnalyticsFacade,
    private appService: ApplicationService,
    private feature: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.dockingBar.registerHeading('Set Up AutoPay');
    this.route.queryParams.pipe(take(1)).subscribe(queryParams => {
      this.billAccountNumber = queryParams['billAccount'];
      this.hasError$ = this.store.select(
        AutoPaySelectors.selectAutoPayNotificationHasError(this.billAccountNumber)
      );
    });

    // dispatch analytics on success
    this.hasError$
      .pipe(
        filter(hasError => !hasError),
        take(1)
      )
      .subscribe(() => {
        this.analyticsFacade.trackPage(
          AutomaticPaymentsAnalytics.MyAccountBillingPaymentsMultipleAutomaticPaymentsEditDeleteComplete
        );
      });

    this.copyService.getCopy('billing.autoPayRefactor.deleteSuccessConfirmationBodyText');

    this.deleteSuccessText = this.store.select(userQuery.selectEmailAddress).pipe(
      map(emailAddress =>
        this.appService.isApp(Applications.MYACCOUNT_ADMIN)
          ? this.copyService.getCopy(
              'billing.autoPayRefactor.adminToolDeleteSuccessConfirmationBodyText',
              {
                emailAddress
              }
            )
          : this.copyService.getCopy('billing.autoPayRefactor.deleteSuccessConfirmationBodyText', {
              emailAddress
            })
      )
    );

    this.deleteFailureText = this.copyService.getCopy(
      'billing.autoPayRefactor.deleteEditFailureConfirmationBodyText'
    );

    this.confirmationConfig$ = this.getConfirmationConfig();
  }

  primaryButtonClick(): void {
    this.hasError$.pipe(take(1)).subscribe(hasError => {
      if (hasError) {
        this.tryAgain();
      } else {
        this.makeAPayment();
      }
    });
  }

  teritaryButtonClick(): void {
    this.store.dispatch(fromRouterActions.Go({ path: ['billing'] }));
  }

  tryAgain(): void {
    this.store
      .select(AutoPaySelectors.getAutomaticPayment(this.billAccountNumber))
      .pipe(take(1))
      .subscribe(automaticPayment => {
        this.autoPayDeleteUtil.deleteAutoPay(automaticPayment);
      });
  }

  makeAPayment(): void {
    this.store.dispatch(
      pendingPaymentActions.AddPendingPaymentAction({
        billAccountNumber: this.billAccountNumber
      })
    );
    // ONE TIME PAYMENT ENTRYPOINT
    if (this.feature.isEnabled('simplified_payment')) {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
        })
      );
    } else {
      this.store
        .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
        .pipe(take(1))
        .subscribe(billAccounts => {
          if (billAccounts.length === 1) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
              })
            );
          }
        });
    }
  }

  private getConfirmationConfig(): Observable<ConfirmationConfig> {
    const confirmationHeading = this.appService.isApp(Applications.MYACCOUNT_ADMIN)
      ? this.copyService.getCopy(
          'billing.autoPayRefactor.adminToolDeleteSuccessConfirmationHeadingText'
        )
      : this.copyService.getCopy('billing.autoPayRefactor.deleteSuccessConfirmationHeadingText');

    return this.hasError$.pipe(
      map((hasError: boolean) =>
        hasError
          ? {
              heading: this.copyService.getCopy(
                'billing.autoPayRefactor.deleteFailureConfirmationHeadingText'
              ),
              status: ConfirmationStatus.ERROR,
              ctaConfig: {
                primaryButtonName: 'Try Again',
                tertiaryButtonName: 'Done'
              }
            }
          : {
              heading: confirmationHeading,
              status: ConfirmationStatus.SUCCESS,
              ctaConfig: {
                primaryButtonName: 'Make a Payment',
                tertiaryButtonName: 'Done'
              }
            }
      )
    );
  }
}
